import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { classNames } from '../lib/utils';
import {
  User,
  Bell,
  Shield,
  Globe,
  Database,
  Server,
  Save,
  Loader2,
  CheckCircle,
} from 'lucide-react';

export function SettingsPage() {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState<'profile' | 'notifications' | 'scan' | 'security'>('profile');
  const [isLoading, setIsLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  // Settings state
  const [profile, setProfile] = useState({
    email: user?.email || user?.username || '',
    timezone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
  });

  const [notifications, setNotifications] = useState({
    emailAlerts: true,
    emailDailySummary: false,
    emailWeeklyReport: true,
    slackWebhook: '',
    snsTopicArn: '',
    alertChannels: ['email'],
  });

  const [scanSettings, setScanSettings] = useState({
    scanInterval: 15, // minutes
    maxConcurrentScans: 10,
    defaultRegions: ['us-east-1', 'us-west-2', 'eu-west-1'],
    resourceTypes: {
      ec2: true,
      rds: true,
      lambda: true,
      ebs: true,
      elb: true,
      asg: true,
      s3: true,
    },
    utilizationMetrics: {
      cpu: true,
      memory: true,
      disk: true,
      network: true,
    },
  });

  const [securitySettings, setSecuritySettings] = useState({
    mfaEnabled: true,
    sessionTimeout: 60, // minutes
    apiKeyRotation: 90, // days
    ipWhitelist: '',
  });

  const handleSave = async (_section: string) => {
    setIsLoading(true);
    setSaved(false);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsLoading(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const sections = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'scan', label: 'Scan Settings', icon: Database },
    { id: 'security', label: 'Security', icon: Shield },
  ];

  return (
    <div className="page-settings">
      <header className="page-header">
        <h1 className="page-title">Settings</h1>
      </header>

      {saved && (
        <div className="login-alert" style={{ marginBottom: 'var(--space-4)', background: 'var(--color-primary-light)', borderColor: 'var(--color-primary)', color: 'var(--color-primary-dark)' }}>
          <CheckCircle size={16} /> Settings saved successfully
        </div>
      )}

      <div className="card" style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 0, minHeight: 'calc(100vh - 200px)' }}>
        {/* Sidebar Navigation */}
        <aside className="settings-sidebar" style={{ borderRight: '1px solid var(--surface-border)', padding: 'var(--space-4)' }}>
          <nav aria-label="Settings sections">
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <li key={section.id}>
                    <button
                      className={classNames('settings-nav-item', { active: activeSection === section.id })}
                      onClick={() => setActiveSection(section.id as typeof activeSection)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-3)',
                        padding: 'var(--space-2) var(--space-3)',
                        border: 'none',
                        background: activeSection === section.id ? 'var(--color-primary-soft)' : 'transparent',
                        borderRadius: 'var(--radius-md)',
                        color: activeSection === section.id ? 'var(--color-primary-dark)' : 'var(--slate-700)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 600,
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all var(--tx-fast)',
                      }}
                    >
                      <Icon size={18} style={{ flexShrink: 0 }} />
                      {section.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        {/* Content */}
        <main className="settings-content" style={{ padding: 'var(--space-6)', overflowY: 'auto' }}>
          {/* Profile Section */}
          {activeSection === 'profile' && (
            <form onSubmit={(e) => { e.preventDefault(); handleSave('profile'); }}>
              <h2 className="card-title" style={{ marginBottom: 'var(--space-6)' }}>Profile</h2>

              <div className="form-group">
                <label htmlFor="profile-email" className="form-label">Email</label>
                <input
                  type="email"
                  id="profile-email"
                  className="form-input"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  disabled
                />
                <p className="form-hint">Email cannot be changed here. Contact admin to update.</p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                <div className="form-group">
                  <label htmlFor="timezone" className="form-label">Timezone</label>
                  <select
                    id="timezone"
                    className="form-select"
                    value={profile.timezone}
                    onChange={(e) => setProfile({ ...profile, timezone: e.target.value })}
                  >
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">Eastern Time (ET)</option>
                    <option value="America/Chicago">Central Time (CT)</option>
                    <option value="America/Denver">Mountain Time (MT)</option>
                    <option value="America/Los_Angeles">Pacific Time (PT)</option>
                    <option value="Europe/London">London (GMT/BST)</option>
                    <option value="Europe/Paris">Paris (CET/CEST)</option>
                    <option value="Asia/Tokyo">Tokyo (JST)</option>
                    <option value="Asia/Singapore">Singapore (SGT)</option>
                    <option value="Australia/Sydney">Sydney (AEST/AEDT)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="dateFormat" className="form-label">Date Format</label>
                  <select
                    id="dateFormat"
                    className="form-select"
                    value={profile.dateFormat}
                    onChange={(e) => setProfile({ ...profile, dateFormat: e.target.value })}
                  >
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-6)' }}>
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? <Loader2 size={18} className="spin" /> : <> <Save size={18} /> Save Profile </>}
                </button>
              </div>
            </form>
          )}

          {/* Notifications Section */}
          {activeSection === 'notifications' && (
            <form onSubmit={(e) => { e.preventDefault(); handleSave('notifications'); }}>
              <h2 className="card-title" style={{ marginBottom: 'var(--space-6)' }}>Notification Preferences</h2>

              <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
                <div className="card-content">
                  <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>Email Notifications</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notifications.emailAlerts}
                        onChange={(e) => setNotifications({ ...notifications, emailAlerts: e.target.checked })}
                        style={{ width: 18, height: 18, accentColor: 'var(--color-primary)' }}
                      />
                      <div>
                        <p style={{ fontWeight: 500 }}>Alert Notifications</p>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--slate-500)' }}>Receive email when alerts are triggered</p>
                      </div>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notifications.emailDailySummary}
                        onChange={(e) => setNotifications({ ...notifications, emailDailySummary: e.target.checked })}
                        style={{ width: 18, height: 18, accentColor: 'var(--color-primary)' }}
                      />
                      <div>
                        <p style={{ fontWeight: 500 }}>Daily Cost Summary</p>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--slate-500)' }}>Daily email with cost breakdown</p>
                      </div>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={notifications.emailWeeklyReport}
                        onChange={(e) => setNotifications({ ...notifications, emailWeeklyReport: e.target.checked })}
                        style={{ width: 18, height: 18, accentColor: 'var(--color-primary)' }}
                      />
                      <div>
                        <p style={{ fontWeight: 500 }}>Weekly Report</p>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--slate-500)' }}>Weekly summary of resources, costs, and alerts</p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
                <div className="card-content">
                  <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>Integration Webhooks</h3>
                  <div className="form-group">
                    <label htmlFor="slackWebhook" className="form-label">Slack Webhook URL</label>
                    <input
                      type="text"
                      id="slackWebhook"
                      className="form-input"
                      value={notifications.slackWebhook}
                      onChange={(e) => setNotifications({ ...notifications, slackWebhook: e.target.value })}
                      placeholder="https://hooks.slack.com/services/..."
                    />
                    <p className="form-hint">Incoming webhook URL for Slack notifications</p>
                  </div>
                  <div className="form-group">
                    <label htmlFor="snsTopicArn" className="form-label">SNS Topic ARN</label>
                    <input
                      type="text"
                      id="snsTopicArn"
                      className="form-input"
                      value={notifications.snsTopicArn}
                      onChange={(e) => setNotifications({ ...notifications, snsTopicArn: e.target.value })}
                      placeholder="arn:aws:sns:region:account:topic"
                    />
                    <p className="form-hint">SNS topic for alert notifications (configured in AWS)</p>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-6)' }}>
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? <Loader2 size={18} className="spin" /> : <> <Save size={18} /> Save Notifications </>}
                </button>
              </div>
            </form>
          )}

          {/* Scan Settings Section */}
          {activeSection === 'scan' && (
            <form onSubmit={(e) => { e.preventDefault(); handleSave('scan'); }}>
              <h2 className="card-title" style={{ marginBottom: 'var(--space-6)' }}>Scan Configuration</h2>

              <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
                <div className="card-content">
                  <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>Scan Schedule</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                    <div className="form-group">
                      <label htmlFor="scanInterval" className="form-label">Scan Interval (minutes)</label>
                      <input
                        type="number"
                        id="scanInterval"
                        className="form-input"
                        value={scanSettings.scanInterval}
                        onChange={(e) => setScanSettings({ ...scanSettings, scanInterval: Number(e.target.value) })}
                        min={5}
                        max={1440}
                      />
                      <p className="form-hint">How often to run resource scans (min 5, max 1440)</p>
                    </div>
                    <div className="form-group">
                      <label htmlFor="maxConcurrent" className="form-label">Max Concurrent Scans</label>
                      <input
                        type="number"
                        id="maxConcurrent"
                        className="form-input"
                        value={scanSettings.maxConcurrentScans}
                        onChange={(e) => setScanSettings({ ...scanSettings, maxConcurrentScans: Number(e.target.value) })}
                        min={1}
                        max={50}
                      />
                      <p className="form-hint">Maximum parallel account/region scans</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
                <div className="card-content">
                  <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>Default Regions for New Accounts</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                    {[
                      'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
                      'eu-west-1', 'eu-west-2', 'eu-central-1',
                      'ap-southeast-1', 'ap-southeast-2', 'ap-northeast-1',
                    ].map((region) => (
                      <label
                        key={region}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 'var(--space-1)',
                          padding: 'var(--space-1) var(--space-2)',
                          border: '1px solid var(--slate-300)',
                          borderRadius: 'var(--radius-pill)',
                          fontSize: 'var(--text-xs)',
                          cursor: 'pointer',
                          background: scanSettings.defaultRegions.includes(region) ? 'var(--color-primary-soft)' : 'transparent',
                          borderColor: scanSettings.defaultRegions.includes(region) ? 'var(--color-primary)' : 'var(--slate-300)',
                          color: scanSettings.defaultRegions.includes(region) ? 'var(--color-primary-dark)' : 'var(--slate-700)',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={scanSettings.defaultRegions.includes(region)}
                          onChange={(e) => setScanSettings({
                            ...scanSettings,
                            defaultRegions: e.target.checked
                              ? [...scanSettings.defaultRegions, region]
                              : scanSettings.defaultRegions.filter((r) => r !== region),
                          })}
                          style={{ width: 14, height: 14, accentColor: 'var(--color-primary)' }}
                        />
                        <Globe size={12} /> {region}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
                <div className="card-content">
                  <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>Resource Types to Scan</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-3)' }}>
                    {[
                      { key: 'ec2', label: 'EC2 Instances', icon: Server },
                      { key: 'rds', label: 'RDS Instances', icon: Database },
                      { key: 'lambda', label: 'Lambda Functions', icon: Server },
                      { key: 'ebs', label: 'EBS Volumes', icon: Database },
                      { key: 'elb', label: 'Load Balancers', icon: Server },
                      { key: 'asg', label: 'Auto Scaling Groups', icon: Server },
                      { key: 's3', label: 'S3 Buckets', icon: Database },
                    ].map(({ key, label, icon: Icon }) => (
                      <label
                        key={key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          padding: 'var(--space-3)',
                          border: '1px solid var(--surface-border)',
                          borderRadius: 'var(--radius-md)',
                          cursor: 'pointer',
                          background: scanSettings.resourceTypes[key as keyof typeof scanSettings.resourceTypes] ? 'var(--color-primary-soft)' : 'transparent',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={scanSettings.resourceTypes[key as keyof typeof scanSettings.resourceTypes]}
                          onChange={(e) => setScanSettings({
                            ...scanSettings,
                            resourceTypes: { ...scanSettings.resourceTypes, [key]: e.target.checked },
                          })}
                          style={{ width: 18, height: 18, accentColor: 'var(--color-primary)' }}
                        />
                        <Icon size={18} style={{ color: 'var(--clr-teal)' }} />
                        <span style={{ fontWeight: 500 }}>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
                <div className="card-content">
                  <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>Utilization Metrics to Collect</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-3)' }}>
                    {[
                      { key: 'cpu', label: 'CPU Utilization' },
                      { key: 'memory', label: 'Memory Utilization' },
                      { key: 'disk', label: 'Disk Utilization' },
                      { key: 'network', label: 'Network I/O' },
                    ].map(({ key, label }) => (
                      <label
                        key={key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          padding: 'var(--space-3)',
                          border: '1px solid var(--surface-border)',
                          borderRadius: 'var(--radius-md)',
                          cursor: 'pointer',
                          background: scanSettings.utilizationMetrics[key as keyof typeof scanSettings.utilizationMetrics] ? 'var(--color-primary-soft)' : 'transparent',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={scanSettings.utilizationMetrics[key as keyof typeof scanSettings.utilizationMetrics]}
                          onChange={(e) => setScanSettings({
                            ...scanSettings,
                            utilizationMetrics: { ...scanSettings.utilizationMetrics, [key]: e.target.checked },
                          })}
                          style={{ width: 18, height: 18, accentColor: 'var(--color-primary)' }}
                        />
                        <span style={{ fontWeight: 500 }}>{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-6)' }}>
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? <Loader2 size={18} className="spin" /> : <> <Save size={18} /> Save Scan Settings </>}
                </button>
              </div>
            </form>
          )}

          {/* Security Section */}
          {activeSection === 'security' && (
            <form onSubmit={(e) => { e.preventDefault(); handleSave('security'); }}>
              <h2 className="card-title" style={{ marginBottom: 'var(--space-6)' }}>Security Settings</h2>

              <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
                <div className="card-content">
                  <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>Authentication</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-4)', background: 'var(--slate-50)', borderRadius: 'var(--radius-md)' }}>
                      <div>
                        <p style={{ fontWeight: 600 }}>Multi-Factor Authentication</p>
                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--slate-500)' }}>Required for all admin users</p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <span className="badge badge-success">Enabled</span>
                        <Shield size={16} style={{ color: 'var(--color-primary)' }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
                <div className="card-content">
                  <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>Session Management</h3>
                  <div className="form-group">
                    <label htmlFor="sessionTimeout" className="form-label">Session Timeout (minutes)</label>
                    <input
                      type="number"
                      id="sessionTimeout"
                      className="form-input"
                      value={securitySettings.sessionTimeout}
                      onChange={(e) => setSecuritySettings({ ...securitySettings, sessionTimeout: Number(e.target.value) })}
                      min={15}
                      max={480}
                    />
                    <p className="form-hint">Auto-logout after inactivity (15-480 minutes)</p>
                  </div>
                </div>
              </div>

              <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
                <div className="card-content">
                  <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>API Key Rotation</h3>
                  <div className="form-group">
                    <label htmlFor="apiKeyRotation" className="form-label">Rotation Period (days)</label>
                    <input
                      type="number"
                      id="apiKeyRotation"
                      className="form-input"
                      value={securitySettings.apiKeyRotation}
                      onChange={(e) => setSecuritySettings({ ...securitySettings, apiKeyRotation: Number(e.target.value) })}
                      min={30}
                      max={365}
                    />
                    <p className="form-hint">Recommended: 90 days</p>
                  </div>
                </div>
              </div>

              <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
                <div className="card-content">
                  <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 700, marginBottom: 'var(--space-4)' }}>IP Allowlist (CIDR)</h3>
                  <textarea
                    className="form-input"
                    style={{ minHeight: 100, fontFamily: 'monospace', fontSize: 'var(--text-sm)' }}
                    value={securitySettings.ipWhitelist}
                    onChange={(e) => setSecuritySettings({ ...securitySettings, ipWhitelist: e.target.value })}
                    placeholder="192.168.1.0/24
10.0.0.0/8
203.0.113.0/24"
                  />
                  <p className="form-hint">One CIDR per line. Leave empty to allow all IPs.</p>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-6)' }}>
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  {isLoading ? <Loader2 size={18} className="spin" /> : <> <Save size={18} /> Save Security Settings </>}
                </button>
              </div>
            </form>
          )}
        </main>
      </div>
    </div>
  );
}